import styled, { css } from 'styled-components';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { ButtonBox } from '@/components/UI/Button/Button.styles';
import { Button } from '@/components/UI/Button/Button';
import { getSlideAnimation } from '@/styles/helpers';

export const DrawerBox = styled.div<{
  $isOpen: boolean;
  $background?: string;
}>`
  display: flex;
  flex-direction: column;
  position: fixed;
  overflow: hidden;
  z-index: 10200;
  inset: 0;
  width: 100%;
  background-color: ${({ theme, $background }) =>
    $background || theme.palette.secondary.background};
  padding-bottom: ${({ theme }) =>
    theme.isWebView ? theme.insetsBottom : 0}px;

  &:focus-within {
    padding-bottom: 0;
  }

  ${({ $isOpen }) => getSlideAnimation($isOpen)}
`;

export const DrawerContentBox = styled.div`
  overflow-y: auto;
  height: 100%;
  padding-bottom: 10px;
`;

export const DrawerChildren = styled.div<{ $isHeadFixed: boolean }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: unset;
    align-items: unset;
  }

  ${({ $isHeadFixed }) =>
    $isHeadFixed &&
    css`
      overflow-y: auto;

      body.ios ${DrawerBox}:focus-within & {
        padding-bottom: var(--ios-modal-keyboard-height, 0px);
      }
    `}
`;

export const DrawerBottom = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.background};
  padding: 10px 15px 20px;
`;

export const DrawerItemsName = styled(Typography.Caption).attrs({
  $variant: '1',
})`
  padding: 20px 15px 10px;

  &:first-of-type {
    padding-top: 5px;
  }
`;

export const DrawerItems = styled.div<{
  $isInTheMiddle?: boolean;
  $isLast?: boolean;
}>`
  background-color: ${({ theme }) => theme.palette.primary.background};
  border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
  padding: 0 15px;

  ${({ $isInTheMiddle }) =>
    $isInTheMiddle &&
    css`
      margin: 10px 0;
    `}

  ${({ $isLast }) =>
    $isLast &&
    css`
      margin-top: 10px;
    `}

    &:empty {
    padding: 0;
    border: none;
    margin: 0;
  }
`;

export const DrawerItem = styled.div<{
  $isHead?: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
  $hasBottomMargin?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 55px;
  margin-bottom: ${({ $hasBottomMargin }) => ($hasBottomMargin ? '10px' : 0)};
  background-color: ${({ theme }) => theme.palette.primary.background};
  border-top: 1px solid ${({ theme }) => theme.palette.primary.border};
  font-size: 15px;
  font-weight: 400;
  font-style: normal;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primary.labels};

  ${({ $isHead }) =>
    $isHead
      ? css`
          justify-content: center;
          min-height: 60px;
        `
      : css`
          margin: 0 auto;
        `}

  ${({ $isFirst }) =>
    $isFirst &&
    css`
      border-top: 0;
    `}

  ${({ $isLast }) =>
    $isLast &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
    `}
`;

export const DrawerItemsTitle = styled(Typography.Caption).attrs({
  $variant: '1',
})<{ $hasTopGutter?: boolean }>`
  color: ${({ theme }) => theme.palette.secondary.labels};
  padding: ${({ $hasTopGutter }) =>
    $hasTopGutter ? '20px 15px 10px' : '10px 15px'};
  text-transform: uppercase;
`;

export const DrawerClose = styled(ButtonBox).attrs(() => ({ type: 'button' }))<{
  $isAdaptive?: boolean;
  $position?: 'left' | 'right';
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $position }) =>
    $position === 'left'
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `}

  ${({ $isAdaptive, $position }) =>
    $isAdaptive &&
    css`
      @media (min-width: 744px) {
        ${$position === 'left'
          ? css`
              left: 25px;
            `
          : css`
              right: 25px;
            `}
      }
    `}
`;

export const DrawerLeftActionContainer = styled.div<{ $isAdaptive?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 15px;

  ${({ $isAdaptive }) =>
    $isAdaptive &&
    css`
      @media (min-width: 744px) {
        left: 25px;
      }
    `}
`;

export const DrawerSimpleTitle = styled(Typography.Header).attrs(() => ({
  $variant: '3',
}))<{
  $capitalizeTitle?: boolean;
  $isAdaptive?: boolean;
}>`
  ${({ $isAdaptive }) =>
    $isAdaptive &&
    css`
      @media (min-width: 744px) {
        font-size: 20px;
      }
    `}

  ${({ $capitalizeTitle }) =>
    $capitalizeTitle &&
    css`
      text-transform: capitalize;
    `}
`;

export const DrawerTitle = styled(DrawerSimpleTitle)`
  display: flex;
  align-items: center;
  gap: 10px;

  img {
    object-fit: cover;
  }
`;

export const RightAction = styled(Button)<{
  $isAdaptive?: boolean;
  disabled?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0;

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${({ theme }) => theme.palette.tertiary.labels};
    `}

  ${({ $isAdaptive }) =>
    $isAdaptive &&
    css`
      @media (min-width: 744px) {
        right: 25px;
      }
    `}
`;
